<template>
  <transition name="ae-fade">
    <div
      class="wt-custom-overlay-fields"
      ref="container"
      v-local-no-scroll
      @scroll="handleScroll"
    >
      <div class="wt-custom-overlay-title wt2-sg">
        <span class="wt2-text-xl -line-height">{{
          currentPage.displayText || currentPage.name
        }}</span>
        <span
          v-if="isTextCO"
          class="-change-co-btn wt2-text-n3 wticon wticon-replace wt-cursor-pointer"
          title="Switch overlay"
          @click="toggleSwitchCOPanel"
        ></span>
        <div
          class="import-text-container"
          :style="{ top: importDropdownDistanceFromTop + 'px' }"
        >
          <ImportTextDropdown
            :options="textImportOptions"
            @insert="handleImportText"
            v-if="importDropdownOpen"
            @close="closeImportDropdown"
          />
        </div>
        <transition name="slide-fade">
          <div
            v-if="showCOReplaceMenu"
            :class="{ visible: showCOReplaceMenu }"
            class="wt-asset-editor-action-picker-sub-menu"
            v-click-outside="onClickOutsideCOReplaceMenu"
          >
            <div class="-co-change-title">Choose overlay to switch to:</div>
            <ul ref="list">
              <template v-for="page in allTextCOs">
                <li
                  :key="page.id"
                  :class="{
                    disable:
                      activeCustomOverlays.includes(page.id) &&
                      page.id != deleteCoIDOnApply,
                  }"
                  :title="
                    activeCustomOverlays.includes(page.id)
                      ? 'Overlay already in use, remove it first'
                      : page.title || page.displayText
                  "
                  @click="switchCO(page)"
                >
                  <img :src="mixinSrc(page.icon)" />
                  <span
                    :title="page.title || page.displayText"
                    v-text="page.title || page.displayText"
                  />
                </li>
              </template>
            </ul>
          </div>
        </transition>
      </div>
      <div class="wt-custom-overlay-content" ref="content">
        <template v-for="(field, index) in normalizeFields">
          <template v-if="isArray(field)">
            <transition name="ae-fade" :key="index">
              <div
                class="-collapsible"
                v-show="normalizeFields[index - 1].__expend"
              >
                <custom-overlays-field-content
                  v-for="f in field"
                  @field-ready="actOnAllFieldsReady"
                  ref="fieldContent"
                  @toggle-import-dropdown="toggleImportDropdown"
                  :key="f.name"
                  :field="f"
                  :stage-rect="stageRect"
                  :allowStoryboardingControls="allowStoryboardingControls(f)"
                />
              </div>
            </transition>
          </template>
          <template v-else>
            <custom-overlays-field-content
              @toggle-expend="toggleExpend($event, index)"
              ref="fieldContent"
              @field-ready="actOnAllFieldsReady"
              @toggle-import-dropdown="toggleImportDropdown"
              :stage-rect="stageRect"
              :key="field.name"
              :field="field"
              :allowStoryboardingControls="allowStoryboardingControls(field)"
            />
          </template>
        </template>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Asset Editor's CustomOverlaysFields
 */
import app from 'app';
import CustomOverlaysFieldContent from 'AssetEditor/functionality/CustomOverlays/CustomOverlaysFieldContent.vue';
import PageMixin from 'AssetEditor/pages/PageMixin.vue';
import { hasActiveControl } from 'AssetEditor/functionality/CustomOverlays/objectTrackingApi';
import objectTrackingMixin from 'AssetEditor/functionality/CustomOverlays/mixins/objectTracking';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import ImportTextDropdown from 'components/ImportTextDropdown.vue';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

const toggleClassNoScroll = (el) =>
  el && el.classList.toggle('-no-scroll', el.scrollHeight <= el.clientHeight);

const localNoScroll = function (el) {
  // on update, wait for dom to update before calculate
  app.Vue.nextTick(() => {
    toggleClassNoScroll(el);
  });
};

export default {
  components: {
    CustomOverlaysFieldContent,
    ImportTextDropdown,
  },

  mixins: [PageMixin, objectTrackingMixin],

  directives: {
    localNoScroll,
  },

  beforeDestroy() {
    bus.$off('apply', this.apply);
  },

  created() {
    if (this._switchCOClassifications) {
      this.setTmpPageState({
        userInteractedWithPage: true,
      });
      this.deleteCoIDOnApply = this._switchCOClassifications.oldCO;
    }
  },

  mounted() {
    bus.$on('apply', this.apply);
  },

  props: ['stageRect'],

  data() {
    return {
      importDropdownOpen: false,
      importDropdownDistanceFromTop: 0,
      importDropdownElement: null,
      importDropdownComponent: null,
      fieldsReadyArr: [],
      storyboardingControlledFields: ['TEXT', 'MEDIA', 'CHECKBOX', 'SLIDER'],
      showCOReplaceMenu: false,
      deleteCoIDOnApply: null,
      // importOptions: [
      //   {
      //     key: 'metadata',
      //     label: "Asset's Metadata",
      //     texts: ['Sample asset metadata...'],
      //   },
      //   {
      //     key: 'transcript',
      //     label: 'Transcript',
      //     texts: ['Sample transcript text...'],
      //   },
      //   {
      //     key: 'originalTranscript',
      //     label: 'Original Transcript',
      //     texts: ['Sample original transcript...'],
      //   },
      //   {
      //     key: 'voiceOver',
      //     label: 'Voice-over',
      //     texts: ['Sample voice-over text...'],
      //   },
      //   {
      //     key: 'prompt',
      //     label: 'Prompt',
      //     texts: ['Sample AI prompt...', 'text2...'],
      //   },
      // ],
    };
  },

  computed: {
    activeCustomOverlays() {
      return this.activePages
        .map((page) => {
          return page.custom && page.id;
        })
        .filter((page) => page);
    },
    currentTextFieldClassifications() {
      return this.currentPage.fields
        .map((field) => field.type === 'TEXT' && field.fieldClassification)
        .filter((fieldClassification) => fieldClassification);
    },
    currentFieldClassificationValues() {
      return this.currentPage.fields
        .map((field) => {
          const tmpAgdCustomOverlay = this.tmpAgdCustomOverlays.find(
            (co) => co.id === this.currentPage.id
          );
          if (tmpAgdCustomOverlay && field.fieldClassification) {
            return {
              fieldClassification: field.fieldClassification,
              name: field.name,
              value: tmpAgdCustomOverlay.fields.find(
                (f) => f.name === field.name
              ).value,
            };
          }
        })
        .filter((fieldClassification) => fieldClassification);
    },
    layers() {
      return this.currentPage.layers;
    },

    allTextCOs() {
      return this.currentPresets.filter((co) => {
        const switchingFieldsAvailable = co.fields?.find((field) => {
          return this.currentTextFieldClassifications.includes(
            field.fieldClassification
          );
        });
        return (
          co.id !== this.currentPage.id &&
          co.layers.includes('TEXT') &&
          !!switchingFieldsAvailable
        );
      });
    },

    isTextCO() {
      return this.layers && this.layers.includes('TEXT');
    },

    tmpAgdCustomOverlays: function () {
      return this.tmpAGD.customOverlays;
    },

    currentPresets() {
      return this.themeInfo.presets;
    },

    activeOverlayFields() {
      if (this.AGD.customOverlays && this.AGD.customOverlays.length) {
        const activeCustomOverlay = this.AGD.customOverlays.find(
          (co) => co.id === this.currentPage.id
        );
        if (activeCustomOverlay && !activeCustomOverlay.disabled) {
          return activeCustomOverlay.fields;
        }
      }
      return [];
    },

    /**
     * Fields of the overlay being edited as they stand right now.
     *
     * `activeOverlayFields` reads workModel.agd, which only changes on apply -
     * fine for the values each field component owns, but the show/hide toggle has
     * to reflect the live state.
     */
    tmpOverlayFields() {
      const co = _.find(this.tmpAGD.customOverlays || [], {
        id: this.currentPage.id,
      });

      return (co && co.fields) || [];
    },

    fields() {
      const agdFields = this.activeOverlayFields;
      const tmpFields = this.tmpOverlayFields;
      return _.map(this.currentPage.fields, (field) => {
        return _.cloneWith(field, (o) => {
          const clonedField = _.cloneDeep(o);
          // if has active agd, set the already existed value
          if (agdFields) {
            const agdField = _.find(
              agdFields,
              (f) => f.name === clonedField.name
            );

            if (agdField) {
              if (_.isDefined(agdField.value)) {
                clonedField.value = agdField.value;
              }
              if (_.isDefined(agdField.fieldDataModel)) {
                clonedField.fieldDataModel = Object.assign(
                  {},
                  agdField.fieldDataModel
                );
              }
            }
          }

          // show/hide state of an AREA field, so its row's toggle reflects what
          // is stored rather than always showing the preset default
          if (hasActiveControl(clonedField)) {
            const tmpField = _.find(
              tmpFields,
              (f) => f.name === clonedField.name
            );
            if (tmpField && _.isBoolean(tmpField.active)) {
              clonedField.active = tmpField.active;
            }
          }

          if (this._switchCOClassifications) {
            const _switchCOField = this._switchCOClassifications.values.find(
              (fieldClassification) =>
                fieldClassification.fieldClassification ===
                clonedField.fieldClassification
            );
            if (_switchCOField) {
              clonedField.value = _switchCOField.value;
            }
          }

          if (this.isStoryboard) {
            app.setDefaultCustomoverlayFieldDataModel(clonedField);
          }

          return clonedField;
        });
      });
    },

    normalizeFields() {
      const fields = this.excludeArea(this.fields);
      const status = {
        active: false,
      };

      return _.reduce(
        fields,
        (result, value) => {
          const isRoot = value.type === 'SEPARATOR';
          const isCollapsible = value.isCollapsible;

          if (isRoot) {
            status.active = isCollapsible;
            result.push(value);

            if (status.active) {
              value.__expend = _.isDefined(value.__expend)
                ? value.__expend
                : value.defaultExpand;
              value.__expendable = true;
              value.__guid = app.guid();
            } else {
              value.__expendeble = false;
            }
          } else {
            if (status.active) {
              const lastValue = result[result.length - 1];

              _.isArray(lastValue)
                ? lastValue.push(value)
                : result.push([value]);
            } else {
              result.push(value);
            }
          }

          return result;
        },
        []
      );
    },
    ...mapState('assetEditor', [
      '_switchCOClassifications',
      'pages',
      'groupsWithExclusiveCustomOverlays',
      'isStoryboard',
      'textImportOptions',
    ]),
    ...mapGetters('assetEditor', [
      'activePages',
      'story',
      'isStoryboarding',
      'compareLayers',
      'currentPage',
      'AGD',
      'pageTmpState',
      'tmpAGD',
      'themeInfo',
    ]),
  },

  methods: {
    ...mapMutations('assetEditor', [
      'setAGD',
      'setSwitchCOClassifications',
      'setTmpPageState',
    ]),
    ...mapActions('assetEditor', [
      'changeCurrentPage',
      'ADD_OR_UPDATE_CUSTOM_OVERLAY_FIELD',
      'updatePage',
      'removePage',
    ]),
    closeImportDropdown() {
      if (this.importDropdownComponent) {
        this.importDropdownComponent.importMenuOpened = false;
      }
      this.importDropdownOpen = false;
      this.importDropdownComponent = null;
      this.importDropdownElement = null;
      this.$refs.fieldContent.forEach((field) => {
        if (field.$refs?.fieldComponent?.closeImportDropdown) {
          field.$refs?.fieldComponent.closeImportDropdown();
        }
      });
    },
    calculateImportDropdownDistanceFromTop() {
      if (this.importDropdownElement) {
        this.importDropdownDistanceFromTop =
          this.importDropdownElement.getBoundingClientRect().top -
          this.$refs.container.getBoundingClientRect().top;
      }
    },
    toggleImportDropdown(options) {
      this.closeImportDropdown();
      this.importDropdownOpen = true;
      this.importDropdownElement = options.e;
      this.importDropdownComponent = options.component;
      this.calculateImportDropdownDistanceFromTop();
    },
    handleScroll(e) {
      this.calculateImportDropdownDistanceFromTop();
    },
    handleImportText(importedText) {
      if (this.importDropdownComponent) {
        this.importDropdownComponent.insertText(importedText);
      } else {
        console.log('CO handleImportText - no component');
      }
    },
    switchCO: _.debounce(
      function (newCO) {
        const switchCOClassificationValues = {
          oldCO: this.currentPage.id,
          values: this.currentFieldClassificationValues,
        };
        if (this._switchCOClassifications) {
          switchCOClassificationValues.oldCO =
            this._switchCOClassifications.oldCO;
          switchCOClassificationValues.values = _.uniqBy(
            switchCOClassificationValues.values.concat(
              this._switchCOClassifications.values
            ),
            'fieldClassification'
          );
        }
        console.log(switchCOClassificationValues);
        this.setSwitchCOClassifications(switchCOClassificationValues);
        this.changeCurrentPage(newCO.id);
      },
      {
        leading: true,
        trailing: false,
      }
    ),
    toggleSwitchCOPanel(event) {
      this.showCOReplaceMenu = !this.showCOReplaceMenu;
    },
    onClickOutsideCOReplaceMenu(e) {
      if (
        this.showCOReplaceMenu &&
        !e.target.classList.contains('-change-co-btn')
      ) {
        this.showCOReplaceMenu = false;
      }
    },
    allowStoryboardingControls(field) {
      return (
        this.isStoryboarding &&
        this.storyboardingControlledFields.includes(field.type)
      );
    },

    isArray(field) {
      return _.isArray(field);
    },

    toggleExpend(field, index) {
      if (field.__expendable) {
        const shouldScrollToView = !field.__expend;

        if (shouldScrollToView) {
          const fields = this.$refs.content && this.$refs.content.children;
          const nextEl = _.find(fields, (v, i) => i === index + 1);

          // wait for render of new fields
          this.$nextTick(() => {
            // wait for render of collapsible changes
            this.$nextTick(() => {
              nextEl.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            });
          });
        }

        this.updatePage({
          pageId: this.currentPage.id,
          pageChanges: {
            fields: _.map(_.flatten(this.normalizeFields), (nf) => {
              if (field.__guid === nf.__guid) {
                nf.__expend = !field.__expend;
              }
              return nf;
            }),
          },
        });
      }
    },

    excludeArea(fields) {
      const areaFields = _.filter(fields, (field) => field.type === 'AREA');

      const hasOnlyOneArea = areaFields.length && areaFields.length === 1;

      // a single area needs no row - the stage picker is the only control.
      // unless it carries a show/hide toggle, which lives on that row
      return hasOnlyOneArea
        ? _.filter(
            fields,
            (field) => field.type !== 'AREA' || field.showActiveControl === true
          )
        : fields;
    },

    actOnAllFieldsReady(field) {
      this.fieldsReadyArr.push(field);

      if (this.fields.length === this.fieldsReadyArr.length) {
        this.fieldsReadyArr = [];

        this.$nextTick(() => {
          toggleClassNoScroll(this.$refs.container);
        });
      }
    },

    getThemeInfoPresetsWithoutCurrent() {
      const currentAspectRatio = this.story.aspectRatio;
      const themeInfoWithoutCurrent = _.filter(
        this.story.allThemeInfos,
        (theme) => theme.aspectRatio !== currentAspectRatio
      );

      return _.map(
        _.filter(themeInfoWithoutCurrent, (theme) => theme.presets),
        (theme) => {
          return theme.presets;
        }
      );
    },

    getNewAgdCustomOverlays(cb) {
      if (!_.isFunction(cb)) {
        console.log(
          'CustomOverlaysFields:getNewAgdCustomOverlays: cb is not a function!'
        );
        return;
      }

      const presetsWithoutCurrentAspectRatio =
        this.getThemeInfoPresetsWithoutCurrent();

      const currentPagePreset = this.currentPresets.find(
        (preset) => preset.id === this.currentPage.id
      );
      // first level layer to compare with
      const layer = currentPagePreset.layers && currentPagePreset.layers[0];
      let newAgdCustomOverlays = null;
      let customAgdFilteredByLayer;

      // loop over all none active presets
      for (const presetsForAspectRatio of presetsWithoutCurrentAspectRatio) {
        // compareCustomOverlaysLayers will iterate through all objects in agd.customOverlays. For each, it will
        // try to find a preset with the same id inside presetsForAspectRatio. If it finds one, it'll compare its
        // layer with the given layer, If they match , it will be included in the result.
        customAgdFilteredByLayer = this.compareLayers({
          parentId: 'customOverlays',
          index: 0,
          layer,
          presetsForAspectRatio,
        });

        // if found some custom overlays in agd with the same layer
        if (!_.isEmpty(customAgdFilteredByLayer)) {
          // add only the agd custom overlays that not exist in compareCustomOverlaysLayers
          newAgdCustomOverlays = (
            newAgdCustomOverlays || this.tmpAgdCustomOverlays
          ).reduce((acc, val) => {
            const agdPerLayer = customAgdFilteredByLayer.find(
              (customAgd) => customAgd.id === val.id
            );

            // add to newAgdCustomOverlays only if it's null or it's the
            // current page that trying to update
            if (
              !this.groupsWithExclusiveCustomOverlays.includes(layer) ||
              !agdPerLayer ||
              agdPerLayer.id === currentPagePreset.id
            ) {
              acc.push(Object.assign({}, val));
            }

            return acc;
          }, []);
        }
      }

      const agd = newAgdCustomOverlays || this.tmpAgdCustomOverlays;
      _.each(agd, (tmpAgd) => {
        if (!tmpAgd.timelineItemId) {
          tmpAgd.timelineItemId = app.guid(tmpAgd.id);
        }
      });
      cb(agd);
    },

    backToPreview() {
      this.getNewAgdCustomOverlays(() => {
        this.askSaveChanges(this.pageTmpState.userInteractedWithPage, () =>
          this.ensureObjectTracking()
        );
        this.setSwitchCOClassifications(null);
      });
    },

    apply() {
      this.getNewAgdCustomOverlays((newAgdCustomOverlays) => {
        this.setAGD({
          customOverlays: newAgdCustomOverlays,
        });
        if (
          this.deleteCoIDOnApply &&
          this.deleteCoIDOnApply !== this.currentPage.id
        ) {
          this.removePage(this.deleteCoIDOnApply);
        }
        this.setSwitchCOClassifications(null);
      });
      this.changeCurrentPage('preview');
    },
  },
};
</script>

<style lang="scss" scoped>
.import-text-container {
  position: absolute;
  left: 405px;
  overflow: visible;
}
.-change-co-btn {
  align-self: start;
  padding-top: 10px;
}
.wt-asset-editor-action-picker-sub-menu {
  position: absolute;
  z-index: 9999;
  top: 70px;
  left: 390px;
  height: unset;
  visibility: hidden;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);

  &.visible {
    visibility: visible;
    > ul {
      flex-wrap: wrap;
    }
  }

  .-co-change-title {
    font-size: 18px;
    font-weight: 300;
    padding: 15px 15px 5px;
  }
  ul {
    list-style: none;
    top: 1px;
    left: 0;
    transform: none;
    display: flex;
    max-width: 672px;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 275px;
    padding: {
      left: 15px;
      right: 5px;
      bottom: 5px;
    }

    &.has-scroll {
      padding-left: 14px;

      li {
        margin-right: 8px;
      }
    }

    li {
      cursor: pointer;
      padding: 0;
      height: 83px;
      width: 97px;
      display: flex;
      flex-direction: column;
      margin-right: 10px;
      margin-top: 7px;
      position: relative;
      transition: background-color 0.2s ease;
      text-align: center;

      &:hover:not(.disable) {
        background-color: #e6e6e6;
      }

      &.disable {
        opacity: 0.4;
        cursor: default;
        // pointer-events: none;
      }

      img {
        background-color: #55616f;
        height: 55px;
      }

      span {
        color: #4a4a4a;
        font-size: 13px;
        font-weight: 400;
        margin-top: 6px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
